import { MouseEventHandler } from "react";

import { IgGrayPrimitives } from "@intelligems/ig-design-tokens";
import { IgDivider, IgTypography } from "@intelligems/ig-theme";
import {
  GenericExperienceFe,
  OrganizationDto,
  ScheduleDto,
} from "@intelligems/ig-types";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useCanStartExperience } from "@src/components/LimitAccess/experiment-scope-hooks";
import {
  getExperienceStatusDate,
  getExperienceStatusText,
} from "@src/features/experiments/utils/utils";
import { useDialogs } from "@src/hooks/dialog";
import {
  fullDateTimeConverter,
  getExperienceRunTime,
} from "@src/utils/dateConverters";

interface ExperienceDurationTooltipProps {
  experience: GenericExperienceFe;
  schedules: ScheduleDto[];
  organization: OrganizationDto;
}

interface DurationSectionProps {
  experience: GenericExperienceFe;
  organization: OrganizationDto;
  locale?: string;
}

const StatusSection = ({
  experience,
  organization,
  locale,
}: DurationSectionProps) => {
  const statusText = getExperienceStatusText(experience);
  const statusDate = getExperienceStatusDate(experience);

  return (
    <IgTypography>
      {statusText}{" "}
      {fullDateTimeConverter(
        statusDate,
        organization?.timezone ?? undefined,
        locale
      )}
      <br />
      {experience.status !== "pending" && (
        <>
          {getExperienceRunTime(
            experience.status,
            experience.experienceActions,
            organization?.timezone,
            experience.category
          )}
          <br />
        </>
      )}
    </IgTypography>
  );
};

interface ScheduleLinkProps {
  label: string;
  onClick: MouseEventHandler;
}

const ScheduleLink = ({ label, onClick }: ScheduleLinkProps) => (
  <IgTypography
    onClick={onClick}
    sx={{
      textDecoration: "underline",
      "&:hover": { cursor: "pointer" },
    }}
  >
    {label}
  </IgTypography>
);

interface ScheduleSectionProps {
  schedules: ScheduleDto[];
  organization: OrganizationDto;
  locale?: string;
  canStart: boolean;
  onEditSchedule: () => void;
}

const ScheduleSection = ({
  schedules,
  organization,
  locale,
  canStart,
  onEditSchedule,
}: ScheduleSectionProps) => {
  const startAtSchedule = schedules.find(
    (schedule) => schedule.action === "start"
  );
  const pauseAtSchedule = schedules.find(
    (schedule) => schedule.action === "pause"
  );
  const stopAtSchedule = schedules.find(
    (schedule) => schedule.action === "end"
  );

  return (
    <IgTypography>
      Schedule
      {startAtSchedule && (
        <>
          <br />
          Start:{" "}
          {fullDateTimeConverter(
            startAtSchedule?.executionTime,
            organization?.timezone ?? undefined,
            locale
          )}
        </>
      )}
      {pauseAtSchedule && (
        <>
          <br />
          Pause:{" "}
          {fullDateTimeConverter(
            pauseAtSchedule?.executionTime,
            organization?.timezone ?? undefined,
            locale
          )}
        </>
      )}
      {stopAtSchedule && (
        <>
          <br />
          Stop:{" "}
          {fullDateTimeConverter(
            stopAtSchedule?.executionTime,
            organization?.timezone ?? undefined,
            locale
          )}
        </>
      )}
      <br />
      <br />
      {canStart ? (
        <ScheduleLink label="Edit schedule" onClick={onEditSchedule} />
      ) : null}
    </IgTypography>
  );
};

export const ExperienceDurationTooltip = ({
  experience,
  schedules,
  organization,
}: ExperienceDurationTooltipProps) => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const { openDialog } = useDialogs();

  const { canStart } = useCanStartExperience(experience);

  const locale = storeCurrencyConfig?.value?.locale;

  const statusSection = (
    <StatusSection
      experience={experience}
      organization={organization}
      locale={locale}
    />
  );

  if (schedules.length > 0) {
    return (
      <>
        {statusSection}
        <br />
        <IgDivider
          sx={{
            marginLeft: "-8px",
            marginRight: "-8px",
            backgroundColor: IgGrayPrimitives.A100,
          }}
          variant={"fullWidth"}
        />
        <br />
        <ScheduleSection
          schedules={schedules}
          organization={organization}
          locale={locale}
          canStart={canStart}
          onEditSchedule={() => openDialog("scheduleTest", { experience })}
        />
      </>
    );
  }

  return (
    <>
      {statusSection}
      {experience.status !== "ended" && canStart ? (
        <>
          <br />
          <ScheduleLink
            label="Set schedule"
            onClick={(e) => {
              e.stopPropagation();
              openDialog("scheduleTest", { experience });
            }}
          />
        </>
      ) : null}
    </>
  );
};
